/**
 * ModelPriceEditDialog — modal for changing a single model price.
 *
 * Wired to PATCH /api/model-prices/:id. The server writes an audit
 * row to model_price_history on every change, so the UI just
 * needs to surface the form, validate inline, and invalidate the
 * cache on success.
 *
 * Why a modal not an inline edit:
 *   Price changes carry an audit consequence — operators should
 *   pause and read the values they're committing. The modal makes
 *   that explicit (especially the optional "reason" box). Inline
 *   editing would invite accidental saves.
 *
 * Lending price mode (ADR-0014) — three shapes, edited in place:
 *   - fixed      → one flat financed total.
 *   - per_period → cost + a per-month margin × tenor; the margin is an
 *     absolute amount or a percent of cost (basis points / month).
 *   - per_tenor  → an explicit financed total per offered tenor (ladder).
 * Money fields are edited in MAJOR units and converted to minor on submit;
 * the percent rate is basis points (an integer, no unit conversion).
 *
 * Existing loans are NOT affected by price changes — they snapshot
 * their schedule + principal + deposit at loan-creation time. The
 * note at the top of the dialog reminds the operator of this.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ModelPrice, PerPeriodBasis, UpdateModelPriceRequest } from "@dfp/api-client";
import { Button } from "./Button.js";
import { PerTenorEditor, emptyTenorRow, type PerTenorRow } from "./PerTenorEditor.js";
import { api } from "../lib/api.js";
import { CONTROL_PROVIDERS } from "../lib/device-catalog.js";

interface Props {
  readonly price: ModelPrice;
  readonly onClose: () => void;
}

type Mode = "fixed" | "per_period" | "per_tenor";

export function ModelPriceEditDialog({ price, onClose }: Props): JSX.Element {
  const qc = useQueryClient();
  const factor = decimalsFactor(price.valueCurrency);

  // Money fields are edited in MAJOR units in the form, converted
  // back to minor on submit so the wire payload always matches the
  // DB column. Mirrors how the create form works.
  const [value, setValue] = useState<string>(String(price.valueAmountMinor / factor));
  const [downPayment, setDownPayment] = useState<string>(String(price.downPaymentMinor / factor));
  const [lendingMode, setLendingMode] = useState<Mode>(price.lendingPriceMode);
  // Used by fixed (flat total) and per_period amount (per-month margin).
  const [lendingPrice, setLendingPrice] = useState<string>(
    String(price.lendingPriceMinor / factor),
  );
  const [perPeriodBasis, setPerPeriodBasis] = useState<PerPeriodBasis>(
    price.perPeriodBasis ?? "amount",
  );
  const [perPeriodRateBps, setPerPeriodRateBps] = useState<string>(
    price.perPeriodRateBps != null ? String(price.perPeriodRateBps) : "",
  );
  const [perTenor, setPerTenor] = useState<readonly PerTenorRow[]>(
    price.perTenor.length > 0
      ? price.perTenor.map((e) => ({
          tenorMonths: String(e.tenorMonths),
          total: String(e.totalMinor / factor),
        }))
      : [emptyTenorRow()],
  );
  const [riskClass, setRiskClass] = useState<"A" | "B" | "C">(price.modelRiskClass);
  const [provider, setProvider] = useState<string>(price.controlProvider);
  // Active state is edited HERE only — the list view dropped its
  // inline button to avoid accidental misclicks on a long catalog.
  const [active, setActive] = useState<boolean>(price.active);
  const [reason, setReason] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: UpdateModelPriceRequest) =>
      api.patch<ModelPrice>(`/api/model-prices/${price.id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["model-prices", "list"] });
      void qc.invalidateQueries({ queryKey: ["model-price-history", price.id] });
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to save the price change.");
    },
  });

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    const valueMinor = toMinor(value, factor);
    const downMinor = toMinor(downPayment, factor);
    if (valueMinor === null || downMinor === null) {
      setError("Value and down payment must be non-negative numbers.");
      return;
    }
    if (downMinor > valueMinor) {
      setError("Down payment cannot exceed the value.");
      return;
    }
    const reasonPatch = reason.trim().length > 0 ? { changeReason: reason.trim() } : {};
    const common = {
      value: { amountMinor: valueMinor, currency: price.valueCurrency },
      downPaymentMinor: downMinor,
      modelRiskClass: riskClass,
      controlProvider: provider,
      active,
      ...reasonPatch,
    };

    if (lendingMode === "fixed") {
      const lendingMinor = toMinor(lendingPrice, factor);
      if (lendingMinor === null) {
        setError("Lending price must be a non-negative number.");
        return;
      }
      if (lendingMinor < valueMinor) {
        setError("A fixed lending price must be at least the value.");
        return;
      }
      save.mutate({ ...common, lendingPriceMode: "fixed", lendingPriceMinor: lendingMinor });
      return;
    }

    if (lendingMode === "per_period") {
      if (perPeriodBasis === "percent") {
        const rate = Number(perPeriodRateBps);
        if (!Number.isInteger(rate) || rate <= 0) {
          setError("Per-month rate must be a positive whole number (basis points of cost).");
          return;
        }
        save.mutate({
          ...common,
          lendingPriceMode: "per_period",
          lendingPriceMinor: 0,
          perPeriodBasis: "percent",
          perPeriodRateBps: rate,
        });
        return;
      }
      const marginMinor = toMinor(lendingPrice, factor);
      if (marginMinor === null || marginMinor <= 0) {
        setError("Per-month margin must be a positive number.");
        return;
      }
      save.mutate({
        ...common,
        lendingPriceMode: "per_period",
        lendingPriceMinor: marginMinor,
        perPeriodBasis: "amount",
      });
      return;
    }

    // per_tenor
    const floor = valueMinor - downMinor;
    const parsed: { tenorMonths: number; totalMinor: number }[] = [];
    const seen = new Set<number>();
    for (const row of perTenor) {
      if (row.tenorMonths.trim() === "" && row.total.trim() === "") continue;
      const t = Number(row.tenorMonths);
      const totalMinor = toMinor(row.total, factor);
      if (!Number.isInteger(t) || t <= 0) {
        setError("Each tenor must be a positive whole number of months.");
        return;
      }
      if (seen.has(t)) {
        setError(`Duplicate tenor ${t} in the ladder.`);
        return;
      }
      seen.add(t);
      if (totalMinor === null || totalMinor < floor) {
        setError(`Tenor ${t} total must be at least value − down payment.`);
        return;
      }
      parsed.push({ tenorMonths: t, totalMinor });
    }
    if (parsed.length === 0) {
      setError("Add at least one tenor row.");
      return;
    }
    save.mutate({
      ...common,
      lendingPriceMode: "per_tenor",
      lendingPriceMinor: 0,
      perTenor: parsed,
    });
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg max-h-[95vh] overflow-y-auto rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-simuflex-line px-5 py-3">
          <div>
            <h2 className="font-bold text-simuflex-navy">
              Edit · {price.brand} {price.model}
            </h2>
            <p className="text-[11px] text-simuflex-slate">
              Existing loans keep their original terms — this change applies only to new financing.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] font-bold uppercase tracking-wider text-simuflex-slate hover:text-simuflex-navy"
          >
            Cancel
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-4 space-y-3 text-xs">
          {/* Money fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={`Value (${price.valueCurrency})`}>
              <input
                type="number"
                min={0}
                step={1}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label={`Down payment (${price.valueCurrency})`}>
              <input
                type="number"
                min={0}
                step={1}
                value={downPayment}
                onChange={(e) => setDownPayment(e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Lending mode">
            <div className="flex gap-2 flex-wrap">
              {(["fixed", "per_period", "per_tenor"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setLendingMode(m)}
                  className={
                    "px-3 py-1.5 rounded-full text-xs font-bold border " +
                    (m === lendingMode
                      ? "bg-simuflex-navy text-white border-simuflex-navy"
                      : "bg-white text-simuflex-navy border-simuflex-line")
                  }
                >
                  {m}
                </button>
              ))}
            </div>
          </Field>

          {/* fixed: one flat total */}
          {lendingMode === "fixed" ? (
            <Field label={`Lending price (${price.valueCurrency})`}>
              <input
                type="number"
                min={0}
                step={1}
                value={lendingPrice}
                onChange={(e) => setLendingPrice(e.target.value)}
                className={inputCls}
              />
            </Field>
          ) : null}

          {/* per_period: basis + matching margin field */}
          {lendingMode === "per_period" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Margin basis (per month)">
                <select
                  value={perPeriodBasis}
                  onChange={(e) => setPerPeriodBasis(e.target.value as PerPeriodBasis)}
                  className={inputCls}
                >
                  <option value="amount">Amount / month</option>
                  <option value="percent">Percent of cost / month</option>
                </select>
              </Field>
              {perPeriodBasis === "amount" ? (
                <Field label={`Margin / month (${price.valueCurrency})`}>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={lendingPrice}
                    onChange={(e) => setLendingPrice(e.target.value)}
                    className={inputCls}
                  />
                </Field>
              ) : (
                <Field label="Rate / month (bps of cost)">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={perPeriodRateBps}
                    onChange={(e) => setPerPeriodRateBps(e.target.value)}
                    className={inputCls}
                  />
                </Field>
              )}
            </div>
          ) : null}

          {/* per_tenor: the ladder */}
          {lendingMode === "per_tenor" ? (
            <Field label="Tenor ladder">
              <div className="rounded-xl border border-simuflex-line bg-white p-3">
                <PerTenorEditor
                  rows={perTenor}
                  onChange={setPerTenor}
                  totalLabel={`Total (${price.valueCurrency})`}
                />
              </div>
            </Field>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Risk class">
              <div className="flex gap-1.5">
                {(["A", "B", "C"] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setRiskClass(c)}
                    className={
                      "px-3 py-1.5 rounded-full text-xs font-bold border " +
                      (c === riskClass
                        ? "bg-simuflex-navy text-white border-simuflex-navy"
                        : "bg-white text-simuflex-navy border-simuflex-line")
                    }
                  >
                    {c}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Control provider">
              {/*
                Provider is a closed set — picking from the catalog
                stops operators from typing typos like "android_do"
                that would later silently bypass the lock-control
                registry. Falls through to the raw id when the
                persisted value isn't in the catalog (legacy rows).
              */}
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className={inputCls}
              >
                {CONTROL_PROVIDERS.find((p) => p.value === provider) ? null : (
                  <option value={provider}>{provider} (legacy)</option>
                )}
                {CONTROL_PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Status">
            {/*
              Segmented Active / Inactive buttons — clearer than a
              checkbox + harder to flip by accident while the user
              is reaching for the Save button.
            */}
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setActive(true)}
                aria-pressed={active}
                className={
                  "px-3 py-1.5 rounded-full text-xs font-bold border " +
                  (active
                    ? "bg-simuflex-green text-white border-simuflex-green"
                    : "bg-white text-simuflex-navy border-simuflex-line")
                }
              >
                Active
              </button>
              <button
                type="button"
                onClick={() => setActive(false)}
                aria-pressed={!active}
                className={
                  "px-3 py-1.5 rounded-full text-xs font-bold border " +
                  (!active
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white text-simuflex-navy border-simuflex-line")
                }
              >
                Inactive
              </button>
              <span className="text-[11px] text-simuflex-slate self-center pl-2">
                {active
                  ? "Visible to dealers in the model dropdown."
                  : "Hidden from new financing. Existing loans keep their terms."}
              </span>
            </div>
          </Field>

          <Field label="Reason for change (optional)">
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. New supplier contract"
              className={inputCls}
            />
            <p className="text-[10px] text-simuflex-slate mt-1">
              Logged onto the audit row alongside who-and-when. Helps later reviewers.
            </p>
          </Field>

          {error ? <p className="text-xs text-red-700">{error}</p> : null}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-simuflex-line">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  Helpers
 * ──────────────────────────────────────────────────────────────────── */

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wider text-simuflex-slate">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputCls =
  "block w-full rounded-lg border border-simuflex-line bg-white px-3 py-2 text-simuflex-navy";

function decimalsFactor(currency: string): number {
  return ["TZS", "KES", "UGX", "RWF", "BIF"].includes(currency) ? 1 : 100;
}

function toMinor(value: string, factor: number): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * factor);
}
